/**
 * Team sizes and game parameters (design doc §12.1) and the power budget (§13.2).
 */

export const MIN_PLAYERS = 6;
export const MAX_PLAYERS = 20;
/** Player count from which the Grand Council (§9.1) and the large-game defaults apply. */
export const GRAND_COUNCIL_MIN_PLAYERS = 15;

export interface SetupRow {
  readonly players: number;
  readonly delegates: number;
  readonly agents: number;
  readonly leakLimit: number;
  readonly poisonVials: number;
  readonly rounds: number;
  readonly grandCouncil: boolean;
}

export interface Range {
  readonly min: number;
  readonly max: number;
}

export interface PowerBudget {
  readonly delegate: Range;
  readonly syndicate: Range;
}

// [players, delegates, agents, leakLimit, vials, rounds]
// Leak limits for 9+ players were raised by 2 after the first simulated balance pass
// (docs/balance/sim-report-001.md).
const TABLE: readonly (readonly [number, number, number, number, number, number])[] = [
  [6, 4, 2, 6, 1, 4],
  [7, 5, 2, 6, 1, 4],
  [8, 6, 2, 7, 1, 5],
  [9, 6, 3, 12, 2, 5],
  [10, 7, 3, 12, 2, 5],
  [11, 8, 3, 12, 2, 5],
  [12, 8, 4, 15, 2, 5],
  [13, 9, 4, 15, 3, 6],
  [14, 10, 4, 15, 3, 6],
  [15, 10, 5, 20, 3, 5],
  [16, 11, 5, 20, 3, 5],
  [17, 12, 5, 20, 3, 5],
  [18, 12, 6, 24, 4, 5],
  [19, 13, 6, 24, 4, 5],
  [20, 14, 6, 24, 4, 5],
];

export const SETUP_TABLE: readonly SetupRow[] = TABLE.map(
  ([players, delegates, agents, leakLimit, poisonVials, rounds]) => ({
    players,
    delegates,
    agents,
    leakLimit,
    poisonVials,
    rounds,
    grandCouncil: players >= GRAND_COUNCIL_MIN_PLAYERS,
  }),
);

// [players, delegate min, delegate max, syndicate min, syndicate max]
const BUDGET: readonly (readonly [number, number, number, number, number])[] = [
  [6, 5, 6, 2, 3],
  [7, 6, 7, 2, 3],
  [8, 7, 8, 3, 4],
  [9, 8, 9, 4, 5],
  [10, 9, 10, 5, 6],
  [11, 10, 11, 5, 6],
  [12, 11, 12, 7, 8],
  [13, 12, 13, 7, 8],
  [14, 12, 13, 7, 8],
  [15, 14, 15, 8, 9],
  [16, 14, 15, 8, 9],
  [17, 15, 16, 8, 9],
  [18, 16, 17, 10, 11],
  [19, 16, 17, 10, 11],
  [20, 17, 18, 10, 11],
];

export const POWER_BUDGET_TABLE: ReadonlyMap<number, PowerBudget> = new Map(
  BUDGET.map(([players, dMin, dMax, sMin, sMax]) => [
    players,
    { delegate: { min: dMin, max: dMax }, syndicate: { min: sMin, max: sMax } },
  ]),
);

export function isSupportedPlayerCount(playerCount: number): boolean {
  return Number.isInteger(playerCount) && playerCount >= MIN_PLAYERS && playerCount <= MAX_PLAYERS;
}

function assertSupported(playerCount: number): void {
  if (!isSupportedPlayerCount(playerCount)) {
    throw new RangeError(
      `Unsupported player count ${playerCount}: must be an integer from ${MIN_PLAYERS} to ${MAX_PLAYERS}`,
    );
  }
}

export function setupFor(playerCount: number): SetupRow {
  assertSupported(playerCount);
  return SETUP_TABLE[playerCount - MIN_PLAYERS] as SetupRow;
}

export function powerBudgetFor(playerCount: number): PowerBudget {
  assertSupported(playerCount);
  return POWER_BUDGET_TABLE.get(playerCount) as PowerBudget;
}

/** Final Tally threshold: `percent` of the Leak limit, rounded up (default 50 %, §4). */
export function finalTallyThreshold(leakLimit: number, percent = 50): number {
  return Math.ceil((leakLimit * percent) / 100);
}
